import React, { useEffect, useRef, useState } from "react";

import PersonIcon from "@mui/icons-material/Person";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PlaceIcon from "@mui/icons-material/Place";
import ReceiptIcon from "@mui/icons-material/Receipt";
import SellIcon from "@mui/icons-material/Sell";
import GrassIcon from "@mui/icons-material/Grass";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// Definición de tabs — orden fijo de las secciones del expediente.
// soloMercadotecnia marca las secciones que sí ve el rol Mercadotecnia, soloCredito las que sí
// ve el rol Crédito (ver CRM_DetalleScreen) — ambos roles restringen a un subconjunto distinto
const TABS = [
  { id: "generales", label: "Datos generales", icon: <PersonIcon />, soloMercadotecnia: true },
  { id: "credito", label: "Info. CyC", icon: <CreditCardIcon /> },
  { id: "ubicacion", label: "Ubicación", icon: <PlaceIcon /> },
  { id: "buro-credito", label: "Buró de crédito", icon: <AccountBalanceIcon />, soloCredito: true },
  { id: "facturacion", label: "Facturación", icon: <ReceiptIcon />, soloMercadotecnia: true },
  { id: "clasificacion", label: "Clasificación", icon: <SellIcon /> },
  { id: "cultivo", label: "Cultivos", icon: <GrassIcon /> },
  { id: "contacto", label: "Contactos", icon: <ContactPhoneIcon /> },
  { id: "asesores", label: "Asesores", icon: <HeadsetMicIcon /> },
  { id: "equip", label: "EQUIP", icon: <VpnKeyIcon /> },
];

// Margen extra (px) debajo de la barra sticky al posicionar una sección
const SPY_GAP = 12;
// Ventana (ms) donde se ignora el scroll-spy tras un click manual en un tab
const SPY_SUPPRESS_MS = 700;

export const CRM_DetalleResumen = ({ esNuevo, esRolMercadotecnia, esRolCredito }) => {
  const tabsVisibles = esRolMercadotecnia
    ? TABS.filter((t) => t.soloMercadotecnia)
    : esRolCredito
      ? TABS.filter((t) => t.soloCredito)
      : TABS;

  const [activeTab, setActiveTab] = useState(tabsVisibles[0].id);
  const [tabsOverflow, setTabsOverflow] = useState(false);

  const resumenRef = useRef(null);
  const tabsScrollRef = useRef(null);
  const suppressUntilRef = useRef(0);

  useEffect(() => {
    const tabsEl = tabsScrollRef.current;
    if (!tabsEl) return undefined;

    const checkOverflow = () => {
      setTabsOverflow(tabsEl.scrollWidth > tabsEl.clientWidth + 1);
    };

    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(tabsEl);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const scrollContainer = document.querySelector(".plantilla-screen-remastered-container");
    if (!scrollContainer) return undefined;

    const handleScroll = () => {
      if (Date.now() < suppressUntilRef.current) return;
      setActiveTab(getSeccionVisible());
    };

    // Sección activa = la que tiene mayor porcentaje de su propia altura visible dentro
    // de la franja debajo de la barra sticky. Usar porcentaje (no área absoluta) evita que
    // una sección alta con solo un remanente visible siga ganando sobre una sección corta
    // (ej. Cultivos, Info. CyC) que ya está 100% visible en el viewport.
    //
    // Además: si alguna sección cabe COMPLETA en la franja (proporción 100%), esa gana
    // siempre sobre cualquier otra solo parcialmente visible — así una sección corta (ej.
    // EQUIP) nunca se salta en favor de la siguiente, que podría alcanzar mayor proporción
    // bruta con solo un remanente visible sin llegar nunca al 100%.
    const getSeccionVisible = () => {
      // Al tocar el fondo absoluto del scroll no hay más contenido para seguir empujando la
      // última sección hacia el centro del viewport (puede quedar corta por debajo del padding
      // final) — se fuerza el último tab directamente.
      const atBottom = scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 2;
      if (atBottom) return tabsVisibles[tabsVisibles.length - 1].id;

      const stickyHeight = resumenRef.current ? resumenRef.current.offsetHeight : 0;
      const containerRect = scrollContainer.getBoundingClientRect();
      const visibleTop = containerRect.top + stickyHeight + SPY_GAP;
      const visibleBottom = containerRect.bottom;

      let mejor = tabsVisibles[0].id;
      let mejorProporcion = -1;
      let mejorCompletaTop = null;

      tabsVisibles.forEach((tab) => {
        const el = document.getElementById(`sec-${tab.id}`);
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const interTop = Math.max(rect.top, visibleTop);
        const interBottom = Math.min(rect.bottom, visibleBottom);
        const area = Math.max(0, interBottom - interTop);
        const proporcion = rect.height > 0 ? area / rect.height : 0;
        const completa = proporcion >= 0.999;

        if (mejorCompletaTop !== null) {
          // Ya hay una sección 100% visible — solo otra sección también completa y más
          // arriba (más cercana a visibleTop) puede reemplazarla
          if (completa && rect.top < mejorCompletaTop) {
            mejorCompletaTop = rect.top;
            mejor = tab.id;
          }
          return;
        }

        if (completa) {
          mejorCompletaTop = rect.top;
          mejor = tab.id;
          return;
        }

        if (proporcion > mejorProporcion) {
          mejorProporcion = proporcion;
          mejor = tab.id;
        }
      });

      return mejor;
    };

    // Al enfocar un campo dentro de una sección, esa sección se marca activa en los tabs
    const handleFocusIn = (e) => {
      const card = e.target.closest(".crm-detalle-card");
      if (!card) return;
      const tab = tabsVisibles.find((t) => `sec-${t.id}` === card.id);
      if (tab) {
        suppressUntilRef.current = Date.now() + SPY_SUPPRESS_MS;
        setActiveTab(tab.id);
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });
    scrollContainer.addEventListener("focusin", handleFocusIn);
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      scrollContainer.removeEventListener("focusin", handleFocusIn);
    };
  }, []);

  const handleTabClick = (id) => {
    setActiveTab(id);
    suppressUntilRef.current = Date.now() + SPY_SUPPRESS_MS;

    const scrollContainer = document.querySelector(".plantilla-screen-remastered-container");
    const el = document.getElementById(`sec-${id}`);
    if (!el || !scrollContainer) return;

    const stickyHeight = resumenRef.current ? resumenRef.current.offsetHeight : 0;
    const containerTop = scrollContainer.getBoundingClientRect().top;
    const elTop = el.getBoundingClientRect().top;
    const nextScrollTop = scrollContainer.scrollTop + (elTop - containerTop) - stickyHeight - SPY_GAP;

    scrollContainer.scrollTo({ top: nextScrollTop, behavior: "smooth" });
  };

  const handleScrollTabs = (direction) => {
    if (!tabsScrollRef.current) return;
    tabsScrollRef.current.scrollBy({ left: direction * 140, behavior: "smooth" });
  };

  return (
    <div className="crm-detalle-resumen" ref={resumenRef}>
      {/* Navegador de tabs (solo aplica con el expediente completo) */}
      {!esNuevo && (
        <div className={`crm-detalle-resumen-tabs${tabsOverflow ? "" : " tabs-distribuidos"}`}>
          {tabsOverflow && (
            <button className="tabs-scroll-btn" onClick={() => handleScrollTabs(-1)}>
              <ChevronLeftIcon />
            </button>
          )}

          <div className="tabs-scroll-container scroll-4" ref={tabsScrollRef}>
            {tabsVisibles.map((tab) => (
              <button
                key={tab.id}
                className={`crm-detalle-tab${activeTab === tab.id ? " tab-active" : ""}`}
                onClick={() => handleTabClick(tab.id)}
              >
                {tab.icon}
                <span className="tab-label">{tab.label}</span>
              </button>
            ))}
          </div>

          {tabsOverflow && (
            <button className="tabs-scroll-btn" onClick={() => handleScrollTabs(1)}>
              <ChevronRightIcon />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
